import React, { useState, useEffect, useRef } from 'react';

const GetReadyPoem = () => {
  // Word sets for the poem - the complete poem that will appear in the background
  const wordSets = [
    // Morning thoughts words
    [
      'How did my left shoe get there?', 
      'I can\'t move until the cat leaves.', 
      'I know I washed underwear this week.', 
      'Bonus task! Clean up cat puke.', 
      'Is rice breakfast?', 
      'If the universe could make me a treasure map for my keys I\'d appreciate it.',
      'Does this shirt match?', 
      'These pants pass the sniff test.', 
      'Maybe I should visit my brother next month.', 
      'I\'d like to be under the sea in an Octopus\'s garden in the shade.',
      'Take Vyvanse.',
      'How long have I been in the shower?'
    ],
  ];

  // State
  const [currentWordSet, setCurrentWordSet] = useState(0);
  const [selectedWords, setSelectedWords] = useState([]);
  const [poemHighlighted, setPoemHighlighted] = useState(false);
  const [wordElements, setWordElements] = useState([]);
  const [vyvanseTaken, setVyvanseTaken] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  
  // Refs
  const containerRef = useRef(null);
  const wordRefs = useRef({});
  const animationFrames = useRef({});
  const wordPhysics = useRef({});
  const popTimers = useRef([]);

  // Update container size on window resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
      }
    };

    // Initial size
    updateSize();
    
    // Add resize listener
    window.addEventListener('resize', updateSize);
    
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  useEffect(() => {
    // Initialize words when component mounts or container size changes
    if (containerSize.width > 0 && containerSize.height > 0) {
      initializeWords();
    }
    
    return () => {
      // Clean up animations and timers on unmount
      Object.values(animationFrames.current).forEach(frameId => {
        cancelAnimationFrame(frameId);
      });
      
      popTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, [containerSize]);

  // When word set changes, reset everything
  useEffect(() => {
    resetWords();
  }, [currentWordSet]);

  // Initialize words from the current set
  const initializeWords = () => {
    if (!containerRef.current) return;
    
    const words = wordSets[currentWordSet];
    const newWordElements = [];
    
    // Clear existing timers
    popTimers.current.forEach(timer => clearTimeout(timer));
    popTimers.current = [];
    
    // Get container dimensions
    const containerWidth = containerSize.width;
    const containerHeight = containerSize.height;
    
    // Adjust layout based on screen size
    const isMobile = window.innerWidth < 768;
    
    // Create word elements
    words.forEach((word, index) => {
      const id = `word-${index}`;
      
      // Position words in a pile at the bottom of the square container
      const pileWidth = containerWidth * 0.8;
      const pileHeight = containerHeight * 0.25; // Use 25% of container height for pile
      
      // Center coordinates for the pile
      const centerX = containerWidth / 2;
      const bottomY = containerHeight;
      
      // Random position within the pile area
      // More concentrated at the bottom
      const xOffset = (Math.random() - 0.5) * pileWidth;
      const x = centerX + xOffset;
      
      // Y position gets higher as you move from the edges to center (arch effect)
      const edgeFactor = 1 - Math.min(1, 2 * Math.abs(xOffset) / pileWidth);
      const heightVariation = edgeFactor * pileHeight * 0.7; // Higher in the middle
      
      // Layer factor (0 = bottom, 1 = top of pile)
      const layerFactor = Math.random() * Math.random(); // Quadratic distribution
      const heightInPile = layerFactor * (pileHeight - heightVariation);
      
      // Final y position (from the top of the container)
      const y = bottomY - heightInPile - heightVariation;
      
      // Rotation based on position (more chaotic at edges)
      const edgeRotationFactor = Math.abs(xOffset) / (pileWidth / 2);
      const rotation = (Math.random() * 30 - 15) * (1 + edgeRotationFactor * 0.5);
      
      // Scale based on layer (smaller at the bottom of the pile)
      const scale = Math.max(0.8, 1 - (layerFactor * 0.2));
      
      // Special case for "Take Vyvanse"
      const isVyvanse = word === 'Take Vyvanse.';
      
      newWordElements.push({
        id,
        text: word,
        state: 'pile',
        position: {
          x: isVyvanse ? centerX + (Math.random() * 80 - 40) : x, // More random position for Vyvanse
          y: isVyvanse ? bottomY - (Math.random() * pileHeight) : y // Random vertical position within the pile
        },
        rotation: (Math.random() * 30 - 15), // Same rotation range as other phrases
        scale: Math.max(0.8, 1 - (Math.random() * 0.2)), // Random scale like other phrases
        zIndex: Math.floor(10 * Math.random()), // Random z-index like other phrases
        selected: false // Track if word has been selected
      });
      
      // Schedule popping with random delays for all phrases
      let popDelay = 500 + Math.random() * (isMobile ? 5000 : 8000);
      
      const timer = setTimeout(() => {
        popWord(id);
      }, popDelay);
      
      popTimers.current.push(timer);
    });
    
    setWordElements(newWordElements);
    setVyvanseTaken(false);
  };

  // Reset words with current word set
  const resetWords = () => {
    // Clean up animations
    Object.values(animationFrames.current).forEach(frameId => {
      cancelAnimationFrame(frameId);
    });
    animationFrames.current = {};
    wordPhysics.current = {};
    
    // Reset selected words
    setSelectedWords([]);
    
    // Initialize new words if container size is available
    if (containerSize.width > 0 && containerSize.height > 0) {
      initializeWords();
    }
    setVyvanseTaken(false);
  };

  // Start a word popping
  const popWord = (id) => {
    // If Vyvanse is taken, don't pop any words
    if (vyvanseTaken) {
      return;
    }
    
    setWordElements(prevWords => {
      // Skip popping if word is no longer in the array or if Vyvanse has been taken
      const wordExists = prevWords.some(word => word.id === id);
      if (!wordExists || vyvanseTaken) return prevWords;
      
      return prevWords.map(word => 
        word.id === id 
          ? { ...word, state: 'popping' } 
          : word
      );
    });
    
    // After popping animation, start physics
    setTimeout(() => {
      // Double-check Vyvanse state before continuing with bounce
      if (vyvanseTaken) return;
      
      setWordElements(prevWords => {
        // Skip if word is no longer in the array or if Vyvanse has been taken
        const wordExists = prevWords.some(word => word.id === id);
        if (!wordExists || vyvanseTaken) return prevWords;
        
        return prevWords.map(word => 
          word.id === id 
            ? { ...word, state: 'bouncing' } 
            : word
        );
      });
      
      // Initialize physics for this word (if still relevant)
      if (wordRefs.current[id] && !vyvanseTaken) {
        startPhysics(id);
      }
    }, 500);
  };

  // Initialize physics for a word
  const startPhysics = (id) => {
    if (!containerRef.current || !wordRefs.current[id] || vyvanseTaken) return;
    
    const wordElement = wordRefs.current[id];
    const rect = wordElement.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Initial position relative to container
    const x = rect.left - containerRect.left + rect.width / 2;
    const y = rect.top - containerRect.top + rect.height / 2;
    
    // Adjust physics parameters for mobile
    const isMobile = window.innerWidth < 768;
    const speedMultiplier = isMobile ? 0.7 : 1.0; // Slower on mobile
    
    // Set up physics properties with more upward initial velocity to pop out of pile
    wordPhysics.current[id] = {
      // Position
      x,
      y,
      // Velocity - adjusted for square container
      vx: (Math.random() * 6 - 3) * speedMultiplier,
      vy: (Math.random() * -14 - 8) * speedMultiplier, // Even stronger upward velocity
      // Rotation
      angle: Math.random() * 20 - 10,
      angularVel: (Math.random() - 0.5) * 0.3 * speedMultiplier,
      // Physics constants - adjusted for square
      gravity: 0.2 * speedMultiplier, // Stronger gravity
      friction: isMobile ? 0.99 : 0.995,
      bounce: isMobile ? 0.82 : 0.88, // More bouncy
      // Element size
      width: rect.width,
      height: rect.height,
      // Energy maintenance
      energy: 1.0,
      lastBounce: 0
    };
    
    // Start animation
    animateWord(id);
  };

  // Animate a word using physics
  const animateWord = (id) => {
    if (vyvanseTaken || !wordPhysics.current[id] || !wordRefs.current[id] || !containerRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    const animate = () => {
      // Skip if element is gone or Vyvanse is taken
      if (vyvanseTaken || !wordRefs.current[id] || !wordPhysics.current[id]) {
        cancelAnimationFrame(animationFrames.current[id]);
        delete animationFrames.current[id];
        return;
      }
      
      const physics = wordPhysics.current[id];
      
      // Apply gravity
      physics.vy += physics.gravity;
      
      // Apply friction
      physics.vx *= physics.friction;
      physics.vy *= physics.friction;
      
      // Energy maintenance system - ensure words keep bouncing
      const timeSinceLastBounce = Date.now() - physics.lastBounce;
      const isSlowMoving = Math.abs(physics.vx) < 0.5 && Math.abs(physics.vy) < 0.5;
      
      // Add random movement to prevent stagnation
      if (isSlowMoving || timeSinceLastBounce > 2000) {
        // Add an upward boost if the word is stagnating
        const randomBoost = Math.random() * 3 + 2;
        physics.vy -= randomBoost; // Strong upward boost
        physics.vx += (Math.random() - 0.5) * 4; // Random horizontal movement
        physics.lastBounce = Date.now();
      } else if (Math.random() < 0.02) {
        // Occasional random movement to keep things lively
        physics.vx += (Math.random() - 0.5) * 1;
        physics.vy += (Math.random() - 0.7) * 1; // Bias upward
      }
      
      // Update position
      physics.x += physics.vx;
      physics.y += physics.vy;
      
      // Update rotation
      physics.angle += physics.angularVel;
      
      // Boundary collision - left/right with bounce
      const padding = 5; // Slight padding to prevent words from touching the edges
      if (physics.x - physics.width/2 < padding) {
        physics.x = physics.width/2 + padding;
        physics.vx = Math.abs(physics.vx) * physics.bounce;
        // Add some vertical energy when hitting sides
        physics.vy += (Math.random() - 0.3) * 2; // Bias toward upward
        physics.angularVel += (Math.random() - 0.5) * 0.1;
        physics.lastBounce = Date.now();
      } else if (physics.x + physics.width/2 > containerWidth - padding) {
        physics.x = containerWidth - physics.width/2 - padding;
        physics.vx = -Math.abs(physics.vx) * physics.bounce;
        // Add some vertical energy when hitting sides
        physics.vy += (Math.random() - 0.3) * 2; // Bias toward upward
        physics.angularVel += (Math.random() - 0.5) * 0.1;
        physics.lastBounce = Date.now();
      }
      
      // Boundary collision - top/bottom with bounce
      if (physics.y - physics.height/2 < padding) {
        physics.y = physics.height/2 + padding;
        physics.vy = Math.abs(physics.vy) * physics.bounce;
        // Add horizontal jitter on ceiling bounce
        physics.vx += (Math.random() - 0.5) * 2;
        physics.angularVel += (Math.random() - 0.5) * 0.1;
        physics.lastBounce = Date.now();
      } else if (physics.y + physics.height/2 > containerHeight - padding) {
        physics.y = containerHeight - physics.height/2 - padding;
        // Stronger upward bounce from floor
        physics.vy = -Math.abs(physics.vy) * physics.bounce * 1.1;
        // Add substantial horizontal movement on floor bounce
        physics.vx += (Math.random() - 0.5) * 3;
        physics.angularVel += (Math.random() - 0.5) * 0.2;
        physics.lastBounce = Date.now();
      }
      
      // Update element position directly in the DOM for smoother animation
      const wordElement = wordRefs.current[id];
      if (wordElement) {
        wordElement.style.left = `${physics.x - physics.width/2}px`;
        wordElement.style.top = `${physics.y - physics.height/2}px`;
        wordElement.style.transform = `rotate(${physics.angle}deg)`;
      }
      
      // Continue animation
      animationFrames.current[id] = requestAnimationFrame(animate);
    };
    
    // Start animation loop
    animationFrames.current[id] = requestAnimationFrame(animate);
  };

  // Handle word selection
  const handleWordClick = (id, text, event) => {
    // Prevent event bubbling if this is from the hit area
    if (event) {
      event.stopPropagation();
    }
    
    // Add to selected words
    setSelectedWords(prev => [...prev, text]);
    
    // Mark word as selected in wordElements
    setWordElements(prevWords => 
      prevWords.map(word => 
        word.id === id ? { ...word, selected: true } : word
      )
    );
    
    // If this is "Take Vyvanse", organize all the words
    if (text === 'Take Vyvanse.') {
      takeVyvanse();
    }
    
    // Highlight poem
    setPoemHighlighted(true);
    setTimeout(() => setPoemHighlighted(false), 300);
    
    // Stop animation
    if (animationFrames.current[id]) {
      cancelAnimationFrame(animationFrames.current[id]);
      delete animationFrames.current[id];
    }
    
    // Clean up physics
    delete wordPhysics.current[id];
  };

  // Take Vyvanse effect - organize all words
  const takeVyvanse = () => {
    // Stop all animations
    Object.values(animationFrames.current).forEach(frameId => {
      cancelAnimationFrame(frameId);
    });
    animationFrames.current = {};
    
    // Set Vyvanse state
    setVyvanseTaken(true);
    
    // Clear all pop timers to prevent any words from popping after Vyvanse is taken
    popTimers.current.forEach(timer => clearTimeout(timer));
    popTimers.current = [];
    
    // Organize all words
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      const isMobile = window.innerWidth < 768;
      
      // Get all words except already selected ones
      setWordElements(prevWords => {
        const availableWords = prevWords.filter(word => !word.selected);
        
        // Calculate grid layout with proper spacing
        const maxCols = isMobile ? 1 : 2; // Single column on mobile
        const totalRows = Math.ceil(availableWords.length / maxCols);
        
        // Calculate safe spacing to prevent overflow
        const verticalPadding = 20;
        const horizontalPadding = 20;
        
        // Calculate available space and item spacing
        const availableHeight = containerHeight - (verticalPadding * 2);
        const availableWidth = containerWidth - (horizontalPadding * 2);
        
        // Calculate row height based on number of items
        // Make sure there's enough space between items
        const rowHeight = Math.min(60, availableHeight / (totalRows || 1));
        const colWidth = availableWidth / maxCols;
        
        // Organize all remaining words in a grid layout
        return availableWords.map((word, index) => {
          // Calculate position based on index
          const col = index % maxCols;
          const row = Math.floor(index / maxCols);
          
          // Calculate x and y position with safety margins
          const x = horizontalPadding + (col * colWidth) + (colWidth / 2);
          
          // Ensure y position doesn't go beyond container boundaries
          // Start items from the top with enough spacing and prevent overlap
          const y = Math.min(
            containerHeight - 40, // Keep items away from bottom edge
            verticalPadding + (row * rowHeight) + 30 // Normal positioning with extra top padding
          );
          
          return {
            ...word,
            state: 'organized',
            organizedPosition: { x, y },
            rotation: 0 // No rotation
          };
        });
      });
      
      // Add visual confirmation
      setPoemHighlighted(true);
      setTimeout(() => setPoemHighlighted(false), 500);
    }
  };

  // Word element component
  const WordElement = ({ word, setRef }) => {
    const isMobile = window.innerWidth < 768;
    
    // Don't render selected words at all - they're only in the background
    if (word.selected) {
      return null;
    }
    
    // Determine styles based on word state and device
    let style = {
      position: 'absolute',
      padding: isMobile ? '6px 10px' : '8px 12px', // Smaller padding for denser pile
      borderRadius: '16px',
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
      zIndex: 10,
      minWidth: isMobile ? '40px' : '60px', // Smaller min-width for denser pile
      textAlign: 'center',
      backgroundColor: word.text === 'Take Vyvanse.' ? '#ff6b6b' : '#2a2a2a',
      color: 'white',
      fontSize: isMobile ? '14px' : '16px',
      lineHeight: '1.2',
      userSelect: 'none',
      willChange: 'transform, left, top',
      transition: word.state === 'organized' ? 'left 0.7s cubic-bezier(0.19, 1, 0.22, 1), top 0.7s cubic-bezier(0.19, 1, 0.22, 1), transform 0.7s cubic-bezier(0.19, 1, 0.22, 1)' : 'none',
      // Ensure text doesn't overflow
      maxWidth: isMobile ? '140px' : '200px',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    };
    
    // Larger hit area for mobile
    const hitAreaSize = isMobile ? 20 : 15;
    
    // Add an invisible larger hit area for better touch/click target
    const hitAreaStyle = {
      position: 'absolute',
      top: `-${hitAreaSize}px`,
      left: `-${hitAreaSize}px`,
      right: `-${hitAreaSize}px`,
      bottom: `-${hitAreaSize}px`,
      backgroundColor: 'transparent',
      borderRadius: '35px',
      zIndex: 5 // Below the word but above other elements
    };
    
    if (word.state === 'pile') {
      // Apply the same styling for all words including "Take Vyvanse"
      const isVyvanse = word.text === 'Take Vyvanse.';
      
      style = {
        ...style,
        left: `${word.position.x}px`,
        top: `${word.position.y}px`,
        transformOrigin: 'center bottom', // Rotate around bottom center
        transform: `rotate(${word.rotation}deg) scale(${word.scale || 1})`,
        opacity: 0.9,
        zIndex: word.zIndex || 5,
        backgroundColor: isVyvanse ? '#ff6b6b' : '#2a2a2a',
        boxShadow: isVyvanse ? '0 3px 10px rgba(255, 107, 107, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.3)'
      };
    } else if (word.state === 'popping') {
      style = {
        ...style,
        left: `${word.position.x}px`,
        top: `${word.position.y}px`,
        transform: `rotate(${word.rotation}deg)`,
        animation: 'pop 0.5s ease-out forwards'
      };
    } else if (word.state === 'bouncing') {
      // Position will be managed by physics animation
      style = {
        ...style,
        position: 'absolute'
      };
    } else if (word.state === 'organized') {
      const wordWidth = isMobile ? 140 : 180; // Slightly smaller on mobile
      
      style = {
        ...style,
        position: 'absolute',
        left: `${word.organizedPosition.x - (wordWidth/2)}px`, // Center horizontally
        top: `${word.organizedPosition.y}px`, // Position at specified y-coordinate
        transform: 'rotate(0deg)',
        opacity: 1,
        width: `${wordWidth}px`, // Fixed width for all organized phrases
        maxWidth: isMobile ? '85%' : '80%', // Ensure not too wide on mobile
        height: 'auto', // Allow height to adjust to content
        minHeight: isMobile ? '30px' : '36px', // Minimum height
        zIndex: 10,
        // Add cleaner styling for organized state
        boxShadow: '0 3px 12px rgba(0, 0, 0, 0.25)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        backgroundColor: '#333',
        marginBottom: isMobile ? '8px' : '12px', // Add bottom margin to prevent overlap
        transition: 'all 0.7s cubic-bezier(0.19, 1, 0.22, 1)' // Smoother, slightly elastic transition
      };
    }
    
    return (
      <div
        ref={el => {
          wordRefs.current[word.id] = el;
          if (setRef) setRef(el);
        }}
        style={style}
        onClick={(e) => handleWordClick(word.id, word.text, e)}
      >
        {word.text}
        {/* Invisible larger hit area for better touch/click target */}
        <div 
          style={hitAreaStyle}
          onClick={(e) => handleWordClick(word.id, word.text, e)}
        />
      </div>
    );
  };

  return (
    <div className="">
      {/* Main layout with fixed reserved space for controls */}
      <div className="">
        {/* Main poem container - now with limited height to leave room for controls */}
        <div className="" style={{ maxHeight: 'calc(100% - 80px)' }}>
          <div
            ref={containerRef}
            className="relative"
            style={{
              width: '95%',
              aspectRatio: '1/1',
              maxWidth: '600px',
              maxHeight: '100%',
              height: '100%',
              backgroundColor: '#some-original-bg-color',
              border: '1px solid #some-original-border-color',
              borderRadius: '8px',
              boxShadow: 'none',
              overflow: 'hidden'
            }}
          >
            <div
              className=""
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300,
                color: '#2a2a2a',
                marginBottom: '12px',
                fontSize: '1.8rem',
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                position: 'relative',
                zIndex: 1
              }}
            >
              ADHD Mornings
            </div>
            {/* Background poem */}
            <div 
              className="absolute z-0 pointer-events-none" 
              style={{
                top: '60px',
                left: '10px',
                right: '10px',
                bottom: '10px',
                overflowY: 'auto'
              }}
            >
              <div
                className={`w-full max-w-full transition-all duration-500 ${
                  vyvanseTaken ? 'opacity-90' : 'opacity-30'
                }`}
              >
                {selectedWords.length > 0 ? (
                  selectedWords.map((line, index) => (
                    <div
                      key={index}
                      className={`my-1 md:my-2 ${
                        line === 'Take Vyvanse.' ? 'font-bold' : 'font-normal'
                      }`}
                      style={{
                        color: vyvanseTaken ? '#original-vyvanse-color' : '#original-base-color',
                        fontSize: vyvanseTaken ? 'original-vyvanse-size' : 'original-base-size',
                        transition: 'all 0.3s'
                      }}
                    >
                      {line}
                    </div>
                  ))
                ) : (
                  <div className="">
                  </div>
                )}
              </div>
            </div>
            
            {/* Bouncing words on top */}
            {wordElements.map(word => (
              <WordElement key={word.id} word={word} />
            ))}
            
            {wordElements.length === 0 && selectedWords.length === 0 && (
              <div className="absolute inset-0">
                Loading thoughts...
              </div>
            )}
            
            {/* Visual indicator when poem is highlighted */}
            {poemHighlighted && (
              <div
                className="absolute inset-0 z-0 pointer-events-none"
                style={{ animation: 'highlight-flash 0.5s ease-out' }}
              />
            )}
          </div>
        </div>
        
        {/* Fixed-height controls section that won't be covered */}
        <div className="" style={{ height: '80px' }}>
          <div className="">
            {selectedWords.length === wordSets[currentWordSet].length ?
              'You are ready for the day!' :
              (selectedWords.length === 0 ?
                'Complete your morning routine by tapping a task.' :
                '')}
          </div>
          <button
            onClick={resetWords}
            className="action-button"
          >
            New Day
          </button>
        </div>
      </div>
      
      {/* Animation keyframes */}
      <style jsx>{`
        @keyframes pop {
          0% {
            transform: scale(1) translateY(0);
          }
          50% {
            transform: scale(1.1) translateY(-25px);
          }
          100% {
            transform: scale(1) translateY(-50px);
          }
        }
        
        @keyframes highlight-flash {
          0% { opacity: 0.3; }
          50% { opacity: 0.1; }
          100% { opacity: 0; }
        }
        
        @keyframes fade-in-slide {
          0% { 
            opacity: 0;
            transform: translateY(10px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 768px) {
          @keyframes pop {
            0% {
              transform: scale(1) translateY(0);
            }
            50% {
              transform: scale(1.1) translateY(-20px);
            }
            100% {
              transform: scale(1) translateY(-40px);
            }
          }
        }
        .action-button {
          background-color: #f4f2eb;
          border: 1px solid rgba(141, 127, 108, 0.5);
          border-radius: 4px;
          padding: 12px 28px;
          font-family: 'Montserrat', sans-serif;
          font-weight: 400;
          font-size: 0.9rem;
          letter-spacing: 1px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        
        .action-button:hover {
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default GetReadyPoem;